import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { warehousesAPI, roomsAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, Package, X, ArrowRight } from "lucide-react";
import WarehouseWorkspaceTabs from "./warehouse/WarehouseWorkspaceTabs";

interface Room { _id: string; name: string; hidden?: boolean; }
interface Unit  { _id: string; roomName: string; notes: string; }
interface Warehouse { _id: string; name: string; location: string; notes: string; units: Unit[]; }

const INPUT = "w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffee-400";

function WarehouseFormModal({ initial, onClose, onSaved }: {
  initial?: Warehouse | null; onClose: () => void; onSaved: (w: Warehouse) => void;
}) {
  const [name, setName] = useState(initial?.name || "");
  const [location, setLocation] = useState(initial?.location || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Vui lòng nhập tên kho"); return; }
    setSaving(true);
    try {
      const res = initial
        ? await warehousesAPI.update(initial._id, { name, location, notes })
        : await warehousesAPI.create({ name, location, notes });
      toast.success(initial ? "Đã cập nhật kho" : "Tạo kho thành công");
      onSaved(res.data.data); onClose();
    } catch { toast.error("Lỗi khi lưu kho"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-zinc-900">{initial ? "Sửa kho" : "Tạo kho mới"}</h2>
          <button onClick={onClose}><X size={20} className="text-zinc-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Tên kho *</label>
            <input className={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="VD: Kho Quận 4" required />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Địa chỉ kho</label>
            <input className={INPUT} value={location} onChange={e => setLocation(e.target.value)} placeholder="VD: 123 Nguyễn Trãi, Q4" />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Ghi chú</label>
            <textarea className={`${INPUT} resize-none`} rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-zinc-200 rounded-lg text-sm text-zinc-600">Huỷ</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-coffee-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
              {saving ? "Đang lưu..." : initial ? "Cập nhật" : "Tạo kho"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UnitFormModal({ warehouseId, initial, usedRooms, onClose, onSaved }: {
  warehouseId: string; initial?: Unit | null; usedRooms: string[];
  onClose: () => void; onSaved: (w: Warehouse) => void;
}) {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomName, setRoomName] = useState(initial?.roomName || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    roomsAPI.getAll().then(r => setRooms(
      (r.data.data || [])
        .filter((rm: Room) => !rm.hidden)
        .sort((a: Room, b: Room) => a.name.localeCompare(b.name, "vi"))
    )).catch(() => {});
  }, []);

  const available = rooms.filter(r => r.name === initial?.roomName || !usedRooms.includes(r.name));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomName) { toast.error("Vui lòng chọn phòng"); return; }
    setSaving(true);
    try {
      const res = initial
        ? await warehousesAPI.updateUnit(warehouseId, initial._id, { roomName, notes })
        : await warehousesAPI.addUnit(warehouseId, { roomName, notes });
      toast.success(initial ? "Đã cập nhật căn" : "Thêm căn thành công");
      onSaved(res.data.data); onClose();
    } catch { toast.error("Lỗi khi lưu căn"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-zinc-900">{initial ? "Sửa căn" : "Thêm căn"}</h2>
          <button onClick={onClose}><X size={20} className="text-zinc-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Phòng *</label>
            <select className={INPUT + " bg-white"} value={roomName} onChange={e => setRoomName(e.target.value)} required>
              <option value="">— Chọn phòng —</option>
              {available.map(r => <option key={r._id} value={r.name}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Ghi chú</label>
            <input className={INPUT} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Ghi chú thêm..." />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-zinc-200 rounded-lg text-sm text-zinc-600">Huỷ</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
              {saving ? "Đang lưu..." : initial ? "Cập nhật" : "Thêm căn"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function WarehouseCard({ warehouse, allWarehouses, onUpdate, onDelete }: {
  warehouse: Warehouse; allWarehouses: Warehouse[]; onUpdate: (w: Warehouse) => void; onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const [editingWarehouse, setEditingWarehouse] = useState(false);
  const [addingUnit, setAddingUnit] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);

  const usedRooms = warehouse.units.map(u => u.roomName);

  const handleDeleteWarehouse = async () => {
    if (!window.confirm(`Xoá kho "${warehouse.name}"? Tất cả căn và dữ liệu kho sẽ bị xoá.`)) return;
    try { await warehousesAPI.delete(warehouse._id); toast.success("Đã xoá kho"); onDelete(warehouse._id); }
    catch { toast.error("Lỗi khi xoá kho"); }
  };

  const handleDeleteUnit = async (unitId: string, roomName: string) => {
    if (!window.confirm(`Xoá căn "${roomName}" khỏi kho?`)) return;
    try {
      const res = await warehousesAPI.deleteUnit(warehouse._id, unitId);
      toast.success("Đã xoá căn"); onUpdate(res.data.data);
    } catch { toast.error("Lỗi khi xoá căn"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4">
        <div className="w-10 h-10 rounded-xl bg-coffee-100 flex items-center justify-center flex-shrink-0">
          <Package size={20} className="text-coffee-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-zinc-900 text-base">{warehouse.name}</h3>
          {warehouse.location && <p className="text-xs text-zinc-400 mt-0.5">{warehouse.location}</p>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full font-medium">{warehouse.units.length} căn</span>
          <Link to={`/warehouse/${warehouse._id}`}
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-coffee-50 text-coffee-700 border border-coffee-200 rounded-lg hover:bg-coffee-100 font-medium">
            Nhập hàng / Kiểm kho <ArrowRight size={12} />
          </Link>
          <button onClick={() => setEditingWarehouse(true)} className="p-1.5 text-zinc-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg">
            <Pencil size={15} />
          </button>
          <button onClick={handleDeleteWarehouse} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
            <Trash2 size={15} />
          </button>
          <button onClick={() => setExpanded(v => !v)} className="p-1.5 text-zinc-400 rounded-lg">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-zinc-100 px-5 py-4">
          {warehouse.notes && <p className="text-xs text-zinc-400 mb-3 italic">{warehouse.notes}</p>}
          {warehouse.units.length === 0
            ? <p className="text-sm text-zinc-400 py-2">Chưa có căn nào trong kho này</p>
            : (
              <div className="flex flex-wrap gap-2 mb-3">
                {warehouse.units.map(unit => (
                  <div key={unit._id} className="flex items-center gap-2 bg-zinc-50 rounded-xl px-3 py-2 border border-zinc-100">
                    <span className="font-semibold text-zinc-800 text-sm">{unit.roomName}</span>
                    {unit.notes && <span className="text-[11px] text-zinc-400">· {unit.notes}</span>}
                    <button onClick={() => setEditingUnit(unit)} className="text-zinc-300 hover:text-indigo-600 ml-1">
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => handleDeleteUnit(unit._id, unit.roomName)} className="text-zinc-300 hover:text-red-500">
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )
          }
          <button onClick={() => setAddingUnit(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 font-medium">
            <Plus size={14} /> Thêm căn
          </button>
        </div>
      )}

      <WarehouseWorkspaceTabs
        warehouseId={warehouse._id}
        allWarehouses={allWarehouses.map(w => ({ _id: w._id, name: w.name }))}
      />

      {editingWarehouse && <WarehouseFormModal initial={warehouse} onClose={() => setEditingWarehouse(false)} onSaved={onUpdate} />}
      {addingUnit && <UnitFormModal warehouseId={warehouse._id} usedRooms={usedRooms} onClose={() => setAddingUnit(false)} onSaved={onUpdate} />}
      {editingUnit && <UnitFormModal warehouseId={warehouse._id} initial={editingUnit} usedRooms={usedRooms} onClose={() => setEditingUnit(null)} onSaved={onUpdate} />}
    </div>
  );
}

export default function WarehousePage() {
  const { user } = useAuth();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const canEdit = ["admin", "superadmin", "kiemkho"].includes(user?.role || "");

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await warehousesAPI.getAll(); setWarehouses(res.data.data); }
    catch { toast.error("Không tải được danh sách kho"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Quản lý kho</h1>
          <p className="text-sm text-zinc-500 mt-1">Quản lý kho và các căn phòng trực thuộc</p>
        </div>
        {canEdit && (
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-coffee-600 text-white rounded-lg text-sm font-semibold hover:bg-coffee-700">
            <Plus size={16} /> Tạo kho
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">Đang tải...</div>
      ) : warehouses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-zinc-400 gap-3">
          <Package size={40} className="text-zinc-300" />
          <p className="font-medium">Chưa có kho nào</p>
          {canEdit && <button onClick={() => setShowCreate(true)} className="text-sm text-coffee-600 hover:underline">+ Tạo kho đầu tiên</button>}
        </div>
      ) : (
        <div className="space-y-4">
          {warehouses.map(w => (
            <WarehouseCard key={w._id} warehouse={w} allWarehouses={warehouses}
              onUpdate={u => setWarehouses(prev => prev.map(x => x._id === u._id ? u : x))}
              onDelete={id => setWarehouses(prev => prev.filter(x => x._id !== id))}
            />
          ))}
        </div>
      )}

      {showCreate && <WarehouseFormModal onClose={() => setShowCreate(false)} onSaved={w => setWarehouses(prev => [...prev, w])} />}
    </div>
  );
}
